
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { GeocodeResult } from '@googlemaps/google-maps-services-js';
import { ValidationObserver } from 'vee-validate';

import GoogleMaps from '../../anonymous/GoogleMaps.vue';

import TextField from '../../../../framework/fields/TextField.vue';
import AutoCompleteField from '../../../../framework/fields/AutoCompleteField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import EmailField from '../../../../framework/fields/EmailField.vue';

import { sharedState } from '../../../../framework/state';
import { getEnumerationOptions } from '../../../../framework/service/options';
import { getResource, postResource } from '../../../../framework/client/resource';

import { VendorType } from '../../../../../common/application/model/enums/VendorType';
import { VendorLocation } from '../../../../../common/application/model/VendorLocation';
import { Vendor } from '../../../../../common/application/model/Vendor';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { parseGeocodeResult } from '../../../../../common/application/service/google_maps_service';

import { getAutoCompleteFieldParams } from '../../../service/query_parameter_service';
import { successToast, errorToast } from '../../../service/toast_service';

@Component({
    components: {
        SelectField,
        TextField,
        AutoCompleteField,
        EmailField,
        ValidationObserver,
        GoogleMaps,
    },
})
export default class AddVendorLocation extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VENDOR_LOCATION;

    // Properties
    @Prop(String) readonly parentId!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as VendorLocation,
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
    };

    // Functions
    async mounted() {
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);

        if (this.parentId) {
            this.local.row = {
                ...this.local.row,
                parentId: this.parentId,
            };
        }
    }

    async add() {
        await this.addResource();
    }

    async addAndClose() {
        await this.addResource(true);
    }

    async addResource(goBack?: boolean) {
        try {
            const newVendorLocation = await postResource<VendorLocation>(this.resourceType, this.local.row);
            successToast(this, 'message.vendorLocationSaved');

            if (goBack) {
                this.back();
            } else {
                this.$router.push(`/vendor-location/${newVendorLocation.id}`);
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    get vendorSelectorParams(): Map<string, string> {
        return getAutoCompleteFieldParams('id', {});
    }

    get fullAddress(): string {
        if (!this.local.row.street) {
            return '';
        }

        return `${this.local.row.street}, ${this.local.row.zip} ${this.local.row.city}`;
    }

    setAddress(geocodeResult: GeocodeResult) {
        const parsedLocation = parseGeocodeResult(geocodeResult);

        this.local.row = {
            ...this.local.row,
            street: parsedLocation.street,
            zip: parsedLocation.zip,
            city: parsedLocation.city,
            country: parsedLocation.country,
            lat: parsedLocation.lat,
            lng: parsedLocation.lng,
        };
    }

    @Watch('local.row.parentId')
    async parentIdChanged(parentId: string) {
        if (!parentId || !parentId.length) {
            return;
        }

        const selectedVendor = await getResource<Vendor>(ApplicationResource.VENDOR, parentId);

        if (!selectedVendor) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            street: selectedVendor.vendorAddressStreet,
            zip: selectedVendor.vendorAddressZip,
            city: selectedVendor.vendorAddressCity,
            country: selectedVendor.vendorAddressCountry,
            vendorType: selectedVendor.vendorType,
        };
    }
}
